import { ReactNode, useState } from 'react'
import { ListItem } from '../../../interfaces/order'
import { updateState } from '../../../services/ordersService'
import { orderStates } from '../../../utils/constants'
import AppModal from '../../atoms/AppModal/AppModal'
import {
    initialOrder,
    updateOrder,
} from '../../../store/slices/order/orderSlice'
import { AppDispatch } from '../../../store/store'
import { useDispatch } from 'react-redux'
import { Order } from '../../../store/interfaces/order'
import ButtonOrderDetailInProcessNavigation from 'components/atoms/ButtonOrderDetailInProcessNavigation/ButtonOrderDetailInProcessNavigation'
import { useNavigate } from 'react-router-dom'
import * as React from 'react'
import ButtonNextNavigation from 'components/atoms/ButtonNextNavigation/ButtonNextNavigation'
import { isSorterMode } from '../../../utils/operationModeUtils'

interface Props {
    component: (data: ListItem) => ReactNode
    list: Order[]
    onOrderStateChanged?: () => void
}

const getButtons = (
    state: orderStates | undefined,
    id: number,
    logoMap: Record<string, (id: number, isLoading: boolean, order: Order) => React.ReactElement>,
    isLoading: boolean,
    order: Order
) => (state && logoMap[state]?.(id, isLoading, order)) || <></>

const OrderList = ({ component: ChildComponent, list, onOrderStateChanged }: Props) => {
    const navigate = useNavigate()
    const [confirmModal, setConfirmModal] = useState(false)
    const [orderSelected, setOrderSelected] = useState<Order>(initialOrder)
    const [loadingOrderId, setLoadingOrderId] = useState<number | null>(null)
    const dispatch: AppDispatch = useDispatch()
    const handleChangeState = () => {
        if (orderSelected.id && orderSelected.state)
            updateState(orderSelected.id, orderSelected.state)
                .then(() => {
                    setConfirmModal(false)
                    setOrderSelected(initialOrder)
                    dispatch(updateOrder(orderSelected))
                    onOrderStateChanged?.()
                })
                .catch((e) => {
                    alert(e.message)
                })
    }
    const handleNavigateToDetail = (id: number) => {
        setLoadingOrderId(id)
        navigate(`/orders/show/details/${id}`)
    }

    const logoMap = {
        IN_PROCESS: (id: number, isLoading: boolean, order: Order) => (
            <>
                <ButtonOrderDetailInProcessNavigation
                    onClick={() => navigate(`/orders/show/details/${id}`)}
                />
                {isSorterMode(order.operationMode || null) && (
                    <ButtonNextNavigation
                        onClick={() => navigate(`/orders/show/${id}`)}
                    />
                )}
            </>
        ),
        PENDING: () => <></>,
        FINISHED: (id: number, isLoading: boolean, order: Order) => (
            <>
                <ButtonOrderDetailInProcessNavigation
                    onClick={() => handleNavigateToDetail(id)}
                    disabled={isLoading}
                />
                {isSorterMode(order.operationMode || null) && (
                    <ButtonNextNavigation
                        onClick={() => navigate(`/orders/show/${id}`)}
                    />
                )}
            </>
        ),
        CANCELED: (id: number, isLoading: boolean, order: Order) => (
            <>
                {isSorterMode(order.operationMode || null) && (
                    <ButtonNextNavigation
                        onClick={() => navigate(`/orders/show/${id}`)}
                    />
                )}
            </>
        ),
        FAILED: (id: number, isLoading: boolean, order: Order) => (
            <>
                {isSorterMode(order.operationMode || null) && (
                    <ButtonNextNavigation
                        onClick={() => navigate(`/orders/show/${id}`)}
                    />
                )}
            </>
        ),
    }

    return (
        <>
            <AppModal
                title={
                    '¿Estás seguro de que deseas realizar esta acción? Esta acción es irreversible y no se podrá deshacer.'
                }
                onClose={() => setConfirmModal(false)}
                handleConfirm={() => handleChangeState()}
                isOpen={confirmModal}
            />
            {list.map((o, index) => {
                return (
                    <ChildComponent
                        id={o.id}
                        key={index}
                        created_at={o.created_at}
                        warehouse_name={o.warehouse_name}
                        description={o.code ? o.code : o.description}
                        state={o.state}
                        operationMode={o.operationMode}
                        packages={o.packages}
                        containers={o.containers}
                        chutes={o.chutes}
                        template={o.template}
                        withoutContainerization={o.withoutContainerization}
                        onChange={(state: orderStates) => {
                            setConfirmModal(true)
                            setOrderSelected({ ...o, state })
                        }}
                    >
                        {getButtons(o.state, o.id, logoMap, loadingOrderId === o.id, o)}
                    </ChildComponent>
                )
            })}
        </>
    )
}

export default OrderList
